import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import PropTypes from 'prop-types';

import { notification } from 'src/components/Notification';
import { JOB_MOBILE_PHOTO_ENTITY_NAME } from 'src/components/JobMobileDataTab/constants';
import { putObjectPreSignedUrl, uploadFileToStorage } from 'src/utils/helpers';
import { useCreateJobAuditSiteData } from 'src/hooks/job';
import Button from 'src/components/Button';
import Loader from 'src/components/Loader';
import logger from 'src/utils/logger';
import Styled from 'src/components/JobMobileDataTab/JobMobilData.styles';

const AddMorePhotos = (props) => {

  const { getPhotoRecordsFromDB, isMobileDataEditable, jobId, jobReportNumber } = props;
  const { t } = useTranslation();
  const [creatingPhotoRecords, setCreatingPhotoRecords] = useState(false);
  const inputFile = useRef(null);

  const filesValidType = (filesArray) => {
    return filesArray.some(file => ['image/jpeg', 'image/png'].includes(file.type));
  };

  const requestPreSignedUrlToUpload = async (fileName, fileSize) => {
    const extendedName = `${jobReportNumber.toLowerCase()}/${fileName}`;
    return putObjectPreSignedUrl(JOB_MOBILE_PHOTO_ENTITY_NAME, extendedName, fileSize);
  };

  const uploadFile = async (file) => {
    const fileExtension = file.type.substring(file.type.lastIndexOf('/') + 1);
    const renamedFile = new File([file],
      `${uuidv4()}.${fileExtension}`,
      {
        type: file.type,
        lastModified: file.lastModified,
      });
    const { url: uploadUrl } = await requestPreSignedUrlToUpload(renamedFile.name, renamedFile.size);
    await uploadFileToStorage(uploadUrl, renamedFile);
    return renamedFile.name;
  };

  const createJobAuditSiteData = useCreateJobAuditSiteData();

  const handleFileSelect = async (event) => {
    const { files } = event.target;

    if (files && files.length > 0) {
      const filesArray = [...files];
      if (!filesValidType(filesArray)) {
        notification.error(t('MOBILE_TAB_IMAGE_MIME_TYPE_ERROR'));
        inputFile.current.value = '';
        return;
      }
      try {
        const newMobileData = { photoRecords: [], geolocationsRecords: [] };
        setCreatingPhotoRecords(true);
        const photoRecords = [];
        await Promise.all(filesArray.map(async (file, index) => {
          const filename = await uploadFile(file);
          photoRecords[index] = filename;
        }));
        // eslint-disable-next-line array-callback-return
        photoRecords.forEach(filename => {
          newMobileData.photoRecords.push({
            filename,
            viewInPhotoForm : true,
          });
        });

        await createJobAuditSiteData(jobId, newMobileData);
        notification.success(t('MOBILE_PHOTO_RECORDS_CREATE_SUCCESS'));
      } catch (err) {
        notification.error(t('MOBILE_PHOTO_RECORDS_CREATE_ERROR'));
        logger.error({ 'Mobile Photo Records Create Error': err });
      } finally {
        setCreatingPhotoRecords(false);
        if (inputFile.current) inputFile.current.value = '';
        await getPhotoRecordsFromDB();
      }
    }
  };
  const onButtonClick = (event) => {
    event.preventDefault();
    if (isMobileDataEditable()) {
      inputFile.current.click();
    }
  };

  return (
    <>
      {
        creatingPhotoRecords ?
          <Loader />
          :
          <>
            <Button gray={!isMobileDataEditable} onClick={onButtonClick}>{t('ADD_MORE_PHOTOS_BTN_TXT')}</Button>
            <Styled.ImageUploadButton>
              <input
                accept="image/png, image/jpeg"
                id="mobile-data-image-picker"
                multiple
                name="fileList"
                onChange={(e) => handleFileSelect(e)}
                ref={inputFile}
                type="file" />
            </Styled.ImageUploadButton>
          </>
      }
    </>
  );
};

AddMorePhotos.propTypes = {
  getPhotoRecordsFromDB: PropTypes.func.isRequired,
  isMobileDataEditable: PropTypes.func.isRequired,
  jobId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  jobReportNumber: PropTypes.string.isRequired,
};

export default React.memo(AddMorePhotos);
